import React, { useCallback, useEffect, useState } from 'react';
import { Pressable, Text, View, StyleSheet } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  setConfigPhase,
  setConfigPlan,
  setSelections,
  clearCellOverrides,
  selectConfigPhase,
  selectConfigSelections,
  selectConfigPlan,
  selectConfigPerCell,
  selectConfigWeekPortions,
  selectConfigTargets,
  setWeekPortions,
  setConfigTargets,
} from '../../../redux/mealPlanUISlice';
import {
  selectCurrentWeekStart,
  setCurrentWeek,
} from '../../../redux/mealPlanSlice';
import { selectRecipeIndex } from '../../../redux/recipeIndexSlice';
import { Paprika } from '../../../styles/theme';
import { ConfigGrid } from './ConfigGrid';
import { GenerationStage, GenerationProgress } from '../GenerationStage';
import { ConfigActionBar, CONFIG_ACTION_BAR_HEIGHT } from './ConfigActionBar';
import { WeekBar } from './WeekBar';
import { FanDrawer } from './FanDrawer';
import { openRecipeDetail } from '../../../utils/recipeNavigation';
import { useGenerateWeek } from '../useGenerateWeek';
import { dayOf, FAN_REASONS } from '../../weekly/v2/constants';
import type { RecipeIndexEntry } from '../../../services/unifiedRecipeService';
import type { FanOption } from './FanDrawer';
import { useTranslation } from 'react-i18next';
import { formatWeekRange } from '../../../utils/dateLocale';
import { addDays } from '../../../utils/localDate';

const shiftWeek = (weekStart: string, delta: number): string =>
  addDays(weekStart, delta * 7);

export function MealConfigDesktop() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { i18n } = useTranslation();
  const router = useRouter();
  const configPhase = useAppSelector(selectConfigPhase);
  const selections = useAppSelector(selectConfigSelections);
  const configPlan = useAppSelector(selectConfigPlan);
  const configPerCell = useAppSelector(selectConfigPerCell);
  const configWeekPortions = useAppSelector(selectConfigWeekPortions);
  const configTargets = useAppSelector(selectConfigTargets);
  const recipeIndex = useAppSelector(selectRecipeIndex);
  const weekStart = useAppSelector(selectCurrentWeekStart);

  const [fanDrawerFor, setFanDrawerFor] = useState<{ sk: number; di: number; options: FanOption[] } | null>(null);

  // Open the standard full recipe-detail page for the recipe in a slot.
  const handleOpenRecipe = useCallback((sk: number) => {
    const recipe = recipeIndex.find((r: RecipeIndexEntry) => r.id === configPlan[sk]);
    if (recipe) openRecipeDetail(router, recipe);
  }, [recipeIndex, configPlan, router]);

  const weekRange = formatWeekRange(weekStart, i18n.language);

  // Config phase = the settings/selection step (before generating). Once
  // generating, the header switches to "Vos suggestions", the settings bar
  // (WeekBar) collapses, and a "Modifier les réglages" button returns here.
  const isConfigPhase = configPhase === 'config';
  const headerTitle = isConfigPhase ? t('mealConfig.planMyWeek') : t('mealConfig.yourSuggestions');
  const headerSub = isConfigPhase
    ? t('mealConfig.configHint')
    : configPhase === 'thinking'
      ? t('mealConfig.affinity.preparing')
      : configPhase === 'cascade'
        ? t('mealConfig.affinity.ready', { count: Object.keys(configPlan).length })
        : t('mealConfig.generatingHint');

  const { generate, cancel } = useGenerateWeek();

  const handleRegenerate = useCallback(() => {
    dispatch(setConfigPhase('config'));
  }, [dispatch]);

  /**
   * Escape aborts a running generation — but only once it is running: in the
   * config phase the open cell popover owns Escape (it closes itself), and a
   * key that both closed the popover and left the screen would be a trap.
   * A drawer open over the cascade gets the key first for the same reason.
   */
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (configPhase !== 'thinking' && configPhase !== 'cascade') return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (fanDrawerFor) { setFanDrawerFor(null); return; }
      cancel();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [configPhase, fanDrawerFor, cancel]);

  const handleOpenFan = useCallback((sk: number) => {
    const currentId = configPlan[sk];
    const usedIds = new Set(Object.values(configPlan));
    const fresh = recipeIndex.filter((r: RecipeIndexEntry) => r.id !== currentId && !usedIds.has(r.id));
    const pool = fresh.length >= 5 ? fresh : recipeIndex.filter((r: RecipeIndexEntry) => r.id !== currentId);
    if (pool.length === 0) return;
    const shuffled = [...pool].sort(() => 0.5 - Math.random()).slice(0, 5);
    const options: FanOption[] = FAN_REASONS.map((reason, i) => ({
      key: reason.key,
      recipe: shuffled[i % shuffled.length],
    }));
    setFanDrawerFor({ sk, di: dayOf(sk), options });
  }, [configPlan, recipeIndex]);

  // Spec: navigating to another week resets the selection and overrides.
  const goWeek = useCallback((delta: number) => {
    dispatch(setCurrentWeek(shiftWeek(weekStart, delta)));
    dispatch(setSelections([]));
    dispatch(clearCellOverrides());
  }, [dispatch, weekStart]);

  return (
    <View style={styles.container}>
      {/* Screen header */}
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <Text style={styles.headerTitle}>{headerTitle}</Text>
          <Text style={styles.headerSub}>{headerSub}</Text>
          <GenerationProgress width={260} />
        </View>
        <View style={styles.weekNav}>
          {!isConfigPhase && (
            <Pressable style={styles.settingsBtn} onPress={() => dispatch(setConfigPhase('config'))}>
              <Feather name="sliders" size={13} color={Paprika.textSecondary} />
              <Text style={styles.settingsBtnText}>{t('thisWeek.editSettings')}</Text>
            </Pressable>
          )}
          <Pressable style={styles.navBtn} onPress={() => goWeek(-1)}>
            <Text style={styles.navBtnText}>‹</Text>
          </Pressable>
          <Text style={styles.weekRange}>{weekRange}</Text>
          <Pressable style={styles.navBtn} onPress={() => goWeek(1)}>
            <Text style={styles.navBtnText}>›</Text>
          </Pressable>
        </View>
      </View>

      {/* Portions & targets bar — only in the settings (config) phase */}
      {isConfigPhase && (
        <WeekBar
          portions={configWeekPortions}
          fish={configTargets.fish}
          veg={configTargets.veg}
          meat={configTargets.meat}
          onPortionsChange={(n) => dispatch(setWeekPortions(n))}
          onFishChange={(n) => dispatch(setConfigTargets({ ...configTargets, fish: n }))}
          onVegChange={(n) => dispatch(setConfigTargets({ ...configTargets, veg: n }))}
          onMeatChange={(n) => dispatch(setConfigTargets({ ...configTargets, meat: n }))}
        />
      )}

      {/* Selection grid — dimmed under « Les affinités » while thinking */}
      <GenerationStage bottomInset={CONFIG_ACTION_BAR_HEIGHT}>
        <ConfigGrid
          phase={configPhase}
          weekStart={weekStart}
          onCellShuffle={handleOpenFan}
          onCellDetail={handleOpenRecipe}
        />
      </GenerationStage>

      {/* Fixed bottom action bar */}
      <ConfigActionBar
        phase={configPhase}
        selCount={selections.length}
        recipeCount={recipeIndex.length}
        onGenerate={generate}
        onRegenerate={handleRegenerate}
        onCancel={cancel}
      />

      {/* Fan drawer — "Autre recette" alternatives */}
      {fanDrawerFor && (
        <FanDrawer
          di={fanDrawerFor.di}
          options={fanDrawerFor.options}
          onSelect={(recipeId) => {
            dispatch(setConfigPlan({ ...configPlan, [fanDrawerFor.sk]: recipeId }));
            setFanDrawerFor(null);
          }}
          onClose={() => setFanDrawerFor(null)}
        />
      )}

    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Paprika.background,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingHorizontal: 24,
    paddingTop: 20,
    paddingBottom: 16,
  } as any,
  headerLeft: {
    flex: 1,
    gap: 4,
  },
  headerTitle: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 28,
    lineHeight: 34,
    color: Paprika.textPrimary,
  },
  headerSub: {
    fontFamily: 'Inter-Regular',
    fontSize: 13,
    color: Paprika.textMuted,
  },
  weekNav: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginLeft: 24,
  },
  settingsBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    borderWidth: 1.5,
    borderColor: Paprika.border,
    borderRadius: 100,
    paddingVertical: 8,
    paddingHorizontal: 14,
    backgroundColor: Paprika.surfaceLowest,
    marginRight: 4,
  },
  settingsBtnText: {
    fontFamily: 'Inter-Medium',
    fontSize: 13,
    color: Paprika.textSecondary,
  },
  weekRange: {
    fontFamily: 'Inter-Medium',
    fontSize: 13,
    color: Paprika.textSecondary,
    minWidth: 120,
    textAlign: 'center',
  },
  navBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    borderWidth: 1.5,
    borderColor: Paprika.border,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: Paprika.surfaceLowest,
  },
  navBtnText: {
    fontFamily: 'Inter-Regular',
    fontSize: 18,
    color: Paprika.textSecondary,
    lineHeight: 22,
  },
});
